import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { store } from '../../../redux/oldStore';
import { ServerResponse } from '../../../utils/Server';
import Icon, { IconType } from '../../Icon/Icon';
import Modal from '../../Modal/Modal';
import { ICustomRole, ICustomRoleCatalog } from './CustomRoleTypes';

const emptyRole = (): ICustomRole => ({
  Id: '',
  Name: '',
  Description: '',
  Grants: [],
});

const CustomRoleManager = () => {
  const [catalog, setCatalog] = useState<ICustomRoleCatalog>();
  const [roles, setRoles] = useState<ICustomRole[]>([]);
  const [selected, setSelected] = useState<ICustomRole>();
  const [showEditor, setShowEditor] = useState(false);

  useEffect(() => {
    store.server.getApi<ServerResponse<ICustomRoleCatalog>>('../CustomRole/Catalog').then((x) => {
      if (x.Success) setCatalog(x.Value);
      else toast.error(x.Message, { autoClose: false });
    });
    loadRoles();
  }, []);

  const loadRoles = () => {
    store.server.getApi<ServerResponse<ICustomRole[]>>('../CustomRole/List').then((x) => {
      if (x.Success) setRoles(x.Value ?? []);
      else toast.error(x.Message, { autoClose: false });
    });
  };

  const roleId = (role: ICustomRole) => role.PublicId || role.Id;

  const hasGrant = (grants: string[] | undefined, key: string) => {
    return (grants ?? []).indexOf(key) >= 0;
  };

  const setGrant = (key: string, on: boolean) => {
    if (!selected) return;
    let grants = [...(selected.Grants ?? [])];
    const page = key.replace(/\.view$|\.manage$/, '');
    if (on) {
      if (grants.indexOf(key) < 0) grants.push(key);
      if (key.endsWith('.manage') && grants.indexOf(page + '.view') < 0) grants.push(page + '.view');
    } else {
      grants = grants.filter((g) => g !== key);
      if (key.endsWith('.view')) grants = grants.filter((g) => g !== page + '.manage');
    }
    setSelected({ ...selected, Grants: grants });
  };

  const save = () => {
    if (!selected) return;
    const errors: string[] = [];
    if (!selected.Name?.trim()) errors.push('Name');
    if (errors.length > 0) {
      toast.warning(
        <>
          Missing required information:
          <ul>
            {errors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        </>
      );
      return;
    }

    store.server
      .postApi<ServerResponse<ICustomRole>>('../CustomRole/Save', {
        Id: selected.Id || null,
        Name: selected.Name.trim(),
        Description: selected.Description,
        Grants: selected.Grants,
      })
      .then((x) => {
        if (x.Success) {
          toast.info('Role saved.');
          setShowEditor(false);
          setSelected(undefined);
          loadRoles();
        } else toast.error(x.Message, { autoClose: false });
      });
  };

  const remove = () => {
    if (!selected?.Id) return;
    if (!window.confirm(`Remove role "${selected.Name}"? People assigned to it will become full Staff.`)) return;
    store.server.postApi<ServerResponse<object>>('../CustomRole/Remove', { id: selected.Id }).then((x) => {
      if (x.Success) {
        toast.info('Role removed.');
        setShowEditor(false);
        setSelected(undefined);
        loadRoles();
      } else toast.error(x.Message, { autoClose: false });
    });
  };

  const grantSummary = (role: ICustomRole) => {
    const grants = role.Grants ?? [];
    const pages = grants.filter((g) => g.endsWith('.view') || g.endsWith('.manage')).length;
    const extras = grants.filter((g) => g.indexOf('.') > 0 && !g.endsWith('.view') && !g.endsWith('.manage')).length;
    const parts = [];
    if (pages) parts.push(`${pages} page grant${pages === 1 ? '' : 's'}`);
    if (extras) parts.push(`${extras} extra${extras === 1 ? '' : 's'}`);
    return parts.join(', ') || 'No grants';
  };

  return (
    <div className="main-body-page">
      <div className="flex-between">
        <h3>Custom Roles</h3>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => {
            setSelected(emptyRole());
            setShowEditor(true);
          }}>
          <Icon type={IconType.plus} /> Add Role
        </button>
      </div>
      <p>
        Custom roles are assigned to Staff accounts and limit which pages they can open and what they can do. Admins and
        unassigned Staff keep full access.
      </p>
      <hr />
      {roles.length === 0 && <div className="alert alert-info">No custom roles yet.</div>}
      {roles.map((role) => (
        <div className="panel panel-default" key={roleId(role)}>
          <div className="panel-body">
            <div className="flex-between">
              <div>
                <h4>{role.Name}</h4>
                {role.Description && <div>{role.Description}</div>}
                <div>
                  {grantSummary(role)}
                  {typeof role.AssignedCount === 'number' && (
                    <>
                      {' '}
                      · {role.AssignedCount} assigned
                    </>
                  )}
                </div>
              </div>
              <button
                type="button"
                className="btn btn-default btn-xs"
                onClick={() => {
                  setSelected({ ...role, Id: roleId(role), Grants: role.Grants ?? [] });
                  setShowEditor(true);
                }}>
                <Icon type={IconType.edit} /> Edit
              </button>
            </div>
          </div>
        </div>
      ))}

      {showEditor && selected && catalog && (
        <Modal setModalOpen={setShowEditor} title={selected.Id ? 'Edit Role' : 'New Role'} size="l">
          <div className="form-horizontal">
            <div className="form-group">
              <label className="control-label col-sm-3" htmlFor="cr-name">
                Name
              </label>
              <div className="col-sm-9">
                <input
                  id="cr-name"
                  className="form-control"
                  value={selected.Name}
                  onChange={(e) => setSelected({ ...selected, Name: e.target.value })}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="control-label col-sm-3" htmlFor="cr-desc">
                Description
              </label>
              <div className="col-sm-9">
                <input
                  id="cr-desc"
                  className="form-control"
                  value={selected.Description ?? ''}
                  onChange={(e) => setSelected({ ...selected, Description: e.target.value })}
                />
              </div>
            </div>

            {catalog.Groups.map((group) => (
              <div key={group.Name} className="form-group">
                <label className="control-label col-sm-3">{group.Name}</label>
                <div className="col-sm-9">
                  <table className="table table-condensed">
                    <thead>
                      <tr>
                        <th>Page</th>
                        <th style={{ width: 80 }}>View</th>
                        <th style={{ width: 90 }}>Manage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.Pages.map((page) => (
                        <tr key={page.Key}>
                          <td>{page.Name}</td>
                          <td>
                            <input
                              type="checkbox"
                              checked={hasGrant(selected.Grants, page.Key + '.view') || hasGrant(selected.Grants, page.Key + '.manage')}
                              onChange={(e) => setGrant(page.Key + '.view', e.target.checked)}
                            />
                          </td>
                          <td>
                            <input
                              type="checkbox"
                              checked={hasGrant(selected.Grants, page.Key + '.manage')}
                              onChange={(e) => setGrant(page.Key + '.manage', e.target.checked)}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}

            <div className="form-group">
              <label className="control-label col-sm-3">Extras</label>
              <div className="col-sm-9">
                {catalog.Extras.map((extra) => (
                  <div className="checkbox" key={extra.Key}>
                    <label>
                      <input
                        type="checkbox"
                        checked={hasGrant(selected.Grants, extra.Key)}
                        onChange={(e) => setGrant(extra.Key, e.target.checked)}
                      />
                      <strong>{extra.Name}</strong>
                      {extra.Description ? ` — ${extra.Description}` : ''}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="form-group">
              <div className="col-sm-9 col-sm-offset-3">
                <button type="button" className="btn btn-primary" onClick={save}>
                  <Icon type={IconType.save} /> Save
                </button>{' '}
                {selected.Id && (
                  <button type="button" className="btn btn-default" onClick={remove}>
                    <Icon type={IconType.delete} /> Remove
                  </button>
                )}
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default CustomRoleManager;
